'use client';

import { useContentfulInspectorMode, useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { PageJournalFragmentFragment, TypeCtaSimple } from '@fm-nx/contentful-types';
import Nav from '../../../../components/nav';
import { useMemo } from 'react';
import { CldImage } from 'next-cloudinary';
import useSlideshowProps from '../../../../../hooks/useSlideshowProps';
import { formatDateLong } from '@fm-nx/ui';
import { lazy } from 'react';
import { PageJournalFragment } from '@fm-nx/contentful';
import dynamic from 'next/dynamic';
const CtaFullSimple = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.CtaFullSimple })));
const ImageResponsiveFull = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.ImageResponsiveFull })));
const SlideshowJournal = lazy(() => import('@fm-nx/ui').then((mod) => ({ default: mod.SlideshowJournal })));
const PageIntroTextDisplay = dynamic(() => import('@fm-nx/ui').then((mod) => ({ default: mod.PageIntroTextDisplay })), {
    ssr: false,
});
const SharePage = dynamic(() => import('@fm-nx/ui').then((mod) => ({ default: mod.SharePage })), {
    ssr: false,
});
const TextRichCentered = dynamic(() => import('@fm-nx/ui').then((mod) => ({ default: mod.TextRichCentered })), {
    ssr: false,
});

interface TagProps {
    title: string;
    id: string;
    href: string;
}

interface JournalDetailProps {
    page: PageJournalFragmentFragment;
    preview: boolean;
}

export default function JournalDetail({ page, preview }: JournalDetailProps) {
    const { slideshowProps } = useSlideshowProps({ page, headerText: 'While we are on the topic...' });
    const inspectorProps = useContentfulInspectorMode();
    const updatedPage = useContentfulLiveUpdates(page, { query: PageJournalFragment });
    const { title, body, publishDate, updatedDate, summary, authorsCollection, heroImage } = updatedPage;

    const tags = useMemo(() => {
        const techTags =
            (updatedPage.technologiesCollection?.items
                .map((topic) => {
                    if (!topic) return null;
                    return {
                        title: topic.title,
                        id: topic.sys.id,
                        href: encodeURI(`/about/journal/technologies/${topic.title}`),
                    };
                })
                .filter((tag) => tag !== null) as TagProps[]) || [];
        const serviceTags =
            (updatedPage.servicesCollection?.items
                .map((topic) => {
                    if (!topic) return null;
                    return {
                        title: topic.title,
                        id: topic.sys.id,
                        href: encodeURI(`/about/journal/services/${topic.title}`),
                    };
                })
                .filter((tag) => tag !== null) as TagProps[]) || [];
        const solutionTags =
            (updatedPage.solutionsCollection?.items
                .map((topic) => {
                    if (!topic) return null;
                    return {
                        title: topic.title,
                        id: topic.sys.id,
                        href: encodeURI(`/about/journal/solutions/${topic.title}`),
                    };
                })
                .filter((tag) => tag !== null) as TagProps[]) || ([] as TagProps[]);
        const companyTags =
            (updatedPage.companyTopicsCollection?.items
                .map((topic) => {
                    if (!topic) return null;
                    return {
                        title: topic.title,
                        id: topic.sys.id,
                        href: encodeURI(`/about/journal/companyTopics/${topic.title}`),
                    };
                })
                .filter((tag) => tag !== null) as TagProps[]) || [];

        return [...techTags, ...serviceTags, ...solutionTags, ...companyTags];
    }, [
        updatedPage.companyTopicsCollection,
        updatedPage.servicesCollection,
        updatedPage.solutionsCollection,
        updatedPage.technologiesCollection,
    ]);

    const formattedDate = formatDateLong(publishDate);
    const formattedUpdatedDate =
        updatedDate && publishDate && new Date(updatedDate) > new Date(publishDate)
            ? formatDateLong(updatedDate)
            : null;

    if (!page) return null;

    const hasAuthors = authorsCollection && authorsCollection.items.length > 0;

    return (
        <div>
            <Nav preview={preview} />
            <div className="pt-20 lg:pt-40">
                <PageIntroTextDisplay
                    overline={`Article${formattedDate ? ` • ${formattedDate}` : ''}${
                        formattedUpdatedDate ? ` • Updated ${formattedUpdatedDate}` : ''
                    }`}
                    headerText={title}
                    headerParagraph={summary}
                />
                <div className="relative w-full mx-auto max-w-container">
                    <div className="mb-10 md:mb-14 md:-mt-16">
                        <div className="block mx-6 md:mx-0 md:grid md:grid-cols-14">
                            <div className="md:col-span-3 md:col-start-2"></div>
                            <div className="col-span-9">
                                <div className="flex items-start">
                                    {authorsCollection?.items.map((author) => {
                                        if (!author) return null;
                                        return (
                                            author?.person?.profileImage?.cloudinaryMedia && (
                                                <div
                                                    key={author.sys.id}
                                                    className={`relative w-18 h-18 shrink-0 mr-4 ${
                                                        (authorsCollection?.items?.length ?? 0) > 1 ? 'first:-mr-4' : ''
                                                    } not-first:-z-10`}
                                                >
                                                    <CldImage
                                                        src={`${(author?.person?.profileImage?.cloudinaryMedia as any[])[0].public_id}`}
                                                        fill
                                                        className="object-cover rounded-full aspect-square"
                                                        alt={`${author?.person?.firstName} ${author?.person?.lastName}`} // Assuming a "name" field exists in the person object
                                                        sizes="144"
                                                    />
                                                </div>
                                            )
                                        );
                                    })}
                                    <div>
                                        {hasAuthors && (
                                            <div>
                                                <span className="flex flex-col gap-1 text-sm uppercase lg:flex-row">
                                                    <span>By</span>
                                                    {authorsCollection?.items.map((author, index) => {
                                                        if (!author || !author.person) return null;
                                                        return (
                                                            <div key={index}>
                                                                {author.isActive ? (
                                                                    <a
                                                                        href={`/about/team/${author.slug}`}
                                                                        className="border-b-out border-b-out--thin"
                                                                    >
                                                                        {`${author.person.firstName} ${author.person.lastName}`}
                                                                    </a>
                                                                ) : (
                                                                    `${author.person.firstName} ${author.person.lastName}`
                                                                )}
                                                                {(authorsCollection?.items?.length ?? 0) > 1 && ' + '}
                                                            </div>
                                                        );
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                        {tags && tags.length > 0 && (
                                            <div className="flex flex-col mt-6 lg:flex-row">
                                                <span className="inline-block mr-1 whitespace-nowrap">Posted in</span>
                                                <ul className="list-none">
                                                    {tags.map((tag, index) => (
                                                        <li className="inline-block mr-1" key={tag.id}>
                                                            <a
                                                                className="border-b-out border-b-out--thin"
                                                                href={tag.href}
                                                            >
                                                                {tag.title}
                                                                {index !== tags.length - 1 ? ', ' : ''}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {heroImage && (
                    <ImageResponsiveFull
                        publicId={heroImage.cloudinaryMedia[0]?.public_id}
                        altText={heroImage.altText || (heroImage.description as string) || `${heroImage.title} Hero`}
                    />
                )}
                <div className="w-full mx-auto mt-16 max-w-container">
                    <div className="mx-6 lg:mx-0">
                        <div className="max-w-2xl container-rich-text">
                            <h2 className="mb-4 overline h5">Share</h2>
                            <SharePage slug={page.slug} pageType={page.__typename} title={title} summary={summary} />
                        </div>
                    </div>
                </div>
                {body && <TextRichCentered textBlock={body.json} />}
                <div className="max-w-2xl container-rich-text">
                    <hr className="mb-8 border-b-2 border-gray-900" />
                    <SharePage slug={page.slug} pageType={page.__typename} title={title} summary={summary} />
                </div>

                <div className="mt-8 md:mt-32">
                    <SlideshowJournal {...slideshowProps} />
                </div>
                <CtaFullSimple
                    ctaSimple={
                        {
                            fields: {
                                internalName: 'Mock CTA',
                                headerText: 'Great things start with a conversation',
                                button: {
                                    fields: {
                                        buttonText: 'Get in touch with us',
                                        linkPath: '/contact',
                                    },
                                },
                            },
                        } as TypeCtaSimple<'WITHOUT_UNRESOLVABLE_LINKS'>
                    }
                />
            </div>
        </div>
    );
}
